/** Pure helpers for sign-in / sign-up flows (testable without React). */

export function shouldUseCookieAuth(params: {
  useHttpOnlyCookies: boolean;
  platform: string;
  apiUrl: string;
  pageHost: string | null;
  hasWindow: boolean;
}): boolean {
  if (!params.useHttpOnlyCookies || params.platform !== 'web') {
    return false;
  }
  if (!params.hasWindow || params.pageHost == null) {
    return true;
  }
  try {
    return new URL(params.apiUrl).host === params.pageHost;
  } catch {
    return false;
  }
}

/** Keep local session when /me fails only if onboarding is still incomplete. */
export function shouldKeepPersistedSessionFallback(
  user: { onboarding_completed?: boolean } | null | undefined
): boolean {
  return !!user && !user.onboarding_completed;
}

export function isVerificationOnlySignUpResponse(response: {
  data?: unknown;
  requires_email_verification?: boolean;
}): boolean {
  return !response.data && !!response.requires_email_verification;
}

export type SignUpMutationSuccess =
  | {
      requiresVerification: true;
      email: string;
      message?: string;
    }
  | {
      user: unknown;
      token: string;
      requiresEmailVerification?: boolean;
    };

export function isVerificationOnlySignUpResult(
  result: unknown
): result is Extract<SignUpMutationSuccess, { requiresVerification: true }> {
  return (
    typeof result === 'object' &&
    result !== null &&
    'requiresVerification' in result &&
    (result as { requiresVerification?: boolean }).requiresVerification === true
  );
}

/** React Query cache updates after a successful sign-up mutation. */
export function getSignUpSuccessCachePatch(data: SignUpMutationSuccess): {
  user: unknown | null;
  session: string | null;
  pendingEmailVerify: boolean;
} {
  if (isVerificationOnlySignUpResult(data)) {
    return { user: null, session: null, pendingEmailVerify: true };
  }
  return {
    user: data.user,
    session: data.token,
    pendingEmailVerify: !!data.requiresEmailVerification,
  };
}

/** React Query cache reset after a failed sign-in / sign-up that wrote session data. */
export function getAuthErrorCachePatch(): {
  user: null;
  session: null;
} {
  return { user: null, session: null };
}

export function shouldNavigateToVerifyEmailAfterSignUp(
  result: unknown
): boolean {
  return isVerificationOnlySignUpResult(result);
}

export function isAuthenticatedWithSession(params: {
  user: unknown | null | undefined;
  token: string | null | undefined;
  cookieAuthMode: boolean;
}): boolean {
  return !!params.user && (!!params.token || params.cookieAuthMode);
}

export function getAuthLinkingPrefixes(envAppUrl?: string): string[] {
  return [
    'http://localhost:19006',
    'https://localhost:19006',
    ...(envAppUrl ? [envAppUrl] : []),
    'https://mycoloursense.com',
    'https://www.mycoloursense.com',
    'https://mycoloursense-app.pages.dev',
    'https://mycoloursense-prod.pages.dev',
  ];
}

export function linkingPrefixMatchesUrl(
  prefixes: string[],
  url: string
): boolean {
  return prefixes.some((prefix) => url.startsWith(prefix));
}
